import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Button, Form, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Container, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Checkbox, Flex } from "antd";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CheckmarkCircle01Icon,
  ViewIcon,
} from "@hugeicons/core-free-icons";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";
import { DATE_TIME_FORMAT } from "@/utils/constants";
import {
  useCompleteNusukIncidentReportMutation,
  useGetNusukIncidentReportsQuery,
  usePrintNusukIncidentReportsForm351Mutation,
} from "@/services/nusukCards/nusukCards";
import { useIncidentReportLookupsQuery } from "@/services/reports";
import { ResolveIncidentReportModal } from "./ResolveIncidentReportModal";
import { useSystem } from "@/utils/hooks/useLocation";
import { useNusukCardsPermissions } from "@/utils/hooks/useNusukCardsPermissions";
import { downloadFile } from "@/utils/helpers";
import { toast } from "react-toastify";

const PAGE_SIZE = 15;

const EMPTY_ROWS = [];

const pickPilgrim = (row) => row?.pilgrim ?? row?.pilgrim_data ?? {};

const getLookupList = (items) => {
  if (Array.isArray(items)) return items;
  return items?.items ?? items?.data ?? [];
};

const pickNationalityLabel = (row, getLocalizedText) => {
  const p = pickPilgrim(row);
  const n = row?.nationality ?? p?.nationality ?? row?.nationality_obj;
  if (n && (n.name_ar != null || n.name_en != null || n.name)) {
    return (
      getLocalizedText(n.name_ar, n.name_en) || n.name || row?.nationality_name || "-"
    );
  }
  if (p?.nationality_ar != null || p?.nationality_en != null) {
    return getLocalizedText(p.nationality_ar, p.nationality_en) || "-";
  }
  return row?.nationality_name ?? "-";
};

const formatCampaignGroup = (row) => {
  const p = pickPilgrim(row);
  const c = p?.campaign_number ?? row?.campaign_number;
  const g = p?.group_number ?? row?.group_number;
  if (c != null && c !== "" && g != null && g !== "") return `${c} / ${g}`;
  if (c != null && c !== "") return String(c);
  if (g != null && g !== "") return String(g);
  return "-";
};

const truncateText = (text, max = 48) => {
  if (text == null || text === "") return "-";
  const s = String(text);
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const formatCampaignGroupColumn = (row) => {
  const cg = formatCampaignGroup(row);
  if (cg !== "-") return cg;
  const code = row?.case_type?.code;
  return code != null && code !== "" ? String(code) : "-";
};

const getIncidentRowId = (row) =>
  row?.id != null ? String(row.id) : "";

/** Same id as `GET {system}/incident-reports/:id` (used by resolve / main reports list). */
const getIncidentReportViewId = (row) =>
  row?.id ?? row?.incident_report_id ?? null;

export const NusukCardsIncidentReportsContent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getLocalizedText, getLocalizedDate } = useLocalizedText();
  const { currentSystem } = useSystem();
  const system = currentSystem || "makkah";
  const {
    canListIncidentReports,
    canResolveIncidentReports,
    canViewExportBatchReport,
  } = useNusukCardsPermissions();
  const { data: lookupsRes } = useIncidentReportLookupsQuery();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch("", 500);

  const reportAuthorityOptions = useMemo(() => {
    const raw =
      lookupsRes?.data?.data?.report_authorities ??
      lookupsRes?.data?.report_authorities ??
      [];
    return getLookupList(raw).map((c) => ({
      value: c.id,
      label:
        getLocalizedText(c.name_ar, c.name_en) ?? c.name_en ?? c.name_ar ?? c.code ?? "",
    }));
  }, [lookupsRes, getLocalizedText]);

  const [selectedIncidentIds, setSelectedIncidentIds] = useState([]);
  const [completeRow, setCompleteRow] = useState(null);
  const [completeRecipient, setCompleteRecipient] = useState("");
  const [completeReportAuthorityId, setCompleteReportAuthorityId] = useState(null);
  const [completeSubmitting, setCompleteSubmitting] = useState(false);
  const [open, setOpen] = useState(false);

  const { mutateAsync: completeIncidentReport } =
    useCompleteNusukIncidentReportMutation();
  const { mutateAsync: printForm351, isPending: isPrinting351 } =
    usePrintNusukIncidentReportsForm351Mutation();

  const apiParams = useMemo(
    () => ({
      page,
      per_page: PAGE_SIZE,
      ...(debouncedValue?.trim() ? { search: debouncedValue.trim() } : {}),
      ...filters,
    }),
    [page, debouncedValue, filters]
  );

  const { data, isLoading, isFetching } = useGetNusukIncidentReportsQuery(apiParams);

  const rows = Array.isArray(data) ? data : (data?.data ?? EMPTY_ROWS);
  const meta =
    (Array.isArray(data) ? {} : data?.meta ?? data?.pagination) ?? {};
  const currentPage = Number(meta.current_page) || page;
  const pageSize = Number(meta.per_page) || PAGE_SIZE;
  const totalRows = Number(meta.total) ?? 0;
  const lastPage = Number(meta.last_page);
  const loading = isLoading || isFetching;

  useEffect(() => {
    if (Number.isFinite(lastPage) && lastPage >= 1 && page > lastPage) {
      setPage(lastPage);
    }
  }, [lastPage, page]);

  const handlePageChange = useCallback((next) => {
    setPage(next);
  }, []);

  const handleSearchChange = useCallback(
    (e) => {
      setSearchValue(e.target.value);
      setPage(1);
    },
    [setSearchValue]
  );

  const handleFilterApply = useCallback((next) => {
    setFilters(next);
    setPage(1);
  }, []);

  const handleFilterReset = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  const handleViewReport = useCallback(
    (row) => {
      const reportId = getIncidentReportViewId(row);
      if (reportId == null || reportId === "") {
        toast.error(t("SOMETHING_WENT_WRONG"));
        return;
      }
      navigate(`/app/${currentSystem}/reports/${reportId}/view`);
    },
    [currentSystem, navigate, t]
  );

  const pageIncidentIds = useMemo(
    () => rows.map((r) => getIncidentRowId(r)).filter(Boolean),
    [rows]
  );
  const allPageIncidentsSelected =
    pageIncidentIds.length > 0 &&
    pageIncidentIds.every((id) => selectedIncidentIds.includes(id));
  const somePageIncidentsSelected =
    !allPageIncidentsSelected &&
    pageIncidentIds.some((id) => selectedIncidentIds.includes(id));

  const toggleSelectIncident = useCallback((id) => {
    if (!id) return;
    setSelectedIncidentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleSelectAllPageIncidents = useCallback(() => {
    setSelectedIncidentIds((prev) => {
      if (allPageIncidentsSelected) {
        return prev.filter((id) => !pageIncidentIds.includes(id));
      }
      return [...new Set([...prev, ...pageIncidentIds])];
    });
  }, [allPageIncidentsSelected, pageIncidentIds]);

  useEffect(() => {
    setSelectedIncidentIds((prev) => (prev.length > 0 ? [] : prev));
  }, [page, debouncedValue, filters]);

  useEffect(() => {
    if (!completeRow?.id) return;
    setCompleteRecipient("");
    setCompleteReportAuthorityId(null);
  }, [completeRow?.id]);

  const closeCompleteModal = useCallback(() => {
    if (completeSubmitting) return;
    setCompleteRow(null);
    setCompleteRecipient("");
    setCompleteReportAuthorityId(null);
  }, [completeSubmitting]);

  const handleSubmitCompleteCase = useCallback(async () => {
    const reportId = completeRow?.id;
    if (reportId == null) return;
    const recipient = completeRecipient.trim();
    if (!recipient) {
      toast.error(t("FIELD_REQUIRED"));
      return;
    }
    if (completeReportAuthorityId == null) {
      toast.error(t("FIELD_REQUIRED"));
      return;
    }
    setCompleteSubmitting(true);
    try {
      await completeIncidentReport({
        reportId,
        recipient_name: recipient,
        report_authority_id: completeReportAuthorityId,
      });
      setCompleteRow(null);
      setCompleteRecipient("");
      setCompleteReportAuthorityId(null);
    } catch {
      /* toast from mutation */
    } finally {
      setCompleteSubmitting(false);
    }
  }, [completeRow, completeRecipient, completeReportAuthorityId, completeIncidentReport, t]);

  const handlePrintForm351 = useCallback(() => {
    const ids = selectedIncidentIds
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n));
    if (!ids.length) {
      toast.error(t("NUSUK_FORM_351_SELECT_FIRST"));
      return;
    }
    const toastId = toast.loading(t("LOADING"));
    printForm351(ids, {
      onSettled: () => toast.dismiss(toastId),
      onSuccess: (res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        downloadFile(blob, `nusuk-form-351-${ids.length}.pdf`);
        toast.success(t("NUSUK_FORM_351_PRINT_SUCCESS"));
      },
      onError: async (err) => {
        let message = t("SOMETHING_WENT_WRONG");
        const data = err?.response?.data;
        if (data instanceof Blob) {
          try {
            const parsed = JSON.parse(await data.text());
            if (parsed?.message) message = parsed.message;
          } catch {
            /* keep default */
          }
        } else if (err?.response?.data?.message) {
          message = err.response.data.message;
        }
        toast.error(message);
      },
    });
  }, [printForm351, selectedIncidentIds, t]);

  const columns = useMemo(
    () => [
      {
        key: "index",
        name: t("SERIAL_NUMBER", { defaultValue: "م" }),
        render: (_, _row, index) => (currentPage - 1) * pageSize + index + 1,
      },
      {
        key: "incident_number",
        name: (
          <Flex gap={8} align="center" className="flex-nowrap">
            <Checkbox
              checked={allPageIncidentsSelected}
              indeterminate={somePageIncidentsSelected}
              onChange={toggleSelectAllPageIncidents}
            />
            <span>{t("INCIDENT_NUMBER")}</span>
          </Flex>
        ),
        render: (_, row) => {
          const id = getIncidentRowId(row);
          return (
            <Flex gap={8} align="center" className="flex-nowrap">
              {id ? (
                <Checkbox
                  checked={selectedIncidentIds.includes(id)}
                  onChange={() => toggleSelectIncident(id)}
                />
              ) : null}
              <span>{row?.incident_number ?? "-"}</span>
            </Flex>
          );
        },
      },
      {
        key: "border_number",
        name: t("PILGRIM_NUMBER"),
        render: (_, row) =>
          row?.pilgrim?.border_number ?? "-",
      },
      {
        key: "batch_no",
        name: t("CARD_NUMBER"),
        render: (_, row) => (
          <span className="text-danger">{row.pilgrim.nusuk_card}</span>
        ),
      },
      {
        key: "pilgrim",
        name: t("PILGRIM_NAME"),
        render: (_, row) => getLocalizedText(row?.pilgrim?.full_name_ar, row?.pilgrim?.full_name_en),
      },
      {
        key: "nationality",
        name: t("NATIONALITY"),
        render: (_, row) => pickNationalityLabel(row, getLocalizedText),
      },
      {
        key: "campaign_group",
        name: t("CAMPAIGN_OR_GROUP_NUMBER"),
        render: (_, row) => formatCampaignGroupColumn(row),
      },
      {
        key: "service_center",
        name: t("SERVICE_CENTER"),
        render: (_, row) => getLocalizedText(row?.service_center?.name_ar, row?.service_center?.name_en),
      },
      {
        key: "description",
        name: t("NUSUK_INCIDENT_STATUS_DESCRIPTION"),
        render: (_, row) => truncateText(row?.description, 56),
      },
      {
        key: "created_at",
        name: t("REPORT_DATE"),
        render: (_, row) => {
          const d = row?.created_at ?? row?.reported_at;
          return d ? getLocalizedDate(d, DATE_TIME_FORMAT) : "-";
        },
      },
      {
        key: "actions",
        name: t("ACTIONS"),
        filterable: false,
      },
    ],
    [
      t,
      getLocalizedText,
      getLocalizedDate,
      currentPage,
      pageSize,
      allPageIncidentsSelected,
      somePageIncidentsSelected,
      selectedIncidentIds,
      toggleSelectAllPageIncidents,
      toggleSelectIncident,
    ]
  );

  const filterFields = useMemo(
    () => [
      {
        name: "border_number",
        label: t("PILGRIM_NUMBER"),
        type: "input",
        placeholder: t("ENTER_PLACEHOLDER"),
        colSize: { md: 6 },
      },
      {
        name: "incident_number",
        label: t("INCIDENT_NUMBER"),
        type: "input",
        placeholder: t("ENTER_PLACEHOLDER"),
        colSize: { md: 6 },
      },
      {
        name: "nusuk_card",
        label: t("NUSUK_CARD_NUMBER"),
        type: "input",
        placeholder: t("ENTER_PLACEHOLDER"),
        colSize: { md: 6 },
      },
      {
        name: "nationality_id",
        label: t("NATIONALITY"),
        type: "select",
        optionKey: "nationalities",
        placeholder: t("CHOOSE"),
        options: (n) => ({
          value: n.id,
          label: getLocalizedText(n.name_ar, n.name_en) || n.name,
        }),
        colSize: { md: 6 },
      },
      {
        name: "campaign_or_group_number",
        label: t("CAMPAIGN_OR_GROUP_NUMBER"),
        type: "input",
        placeholder: t("ENTER_PLACEHOLDER"),
        colSize: { md: 6 },
      },
      {
        name: "service_center_id",
        label: t("SERVICE_CENTER"),
        type: "select",
        optionKey: "service_centers",
        placeholder: t("CHOOSE"),
        options: (s) => ({ value: s.id, label: s.name }),
        colSize: { md: 6 },
      },
      {
        name: "report_date",
        label: t("REPORT_DATE"),
        type: "date",
        dateFormat: "YYYY-MM-DD",
        colSize: { md: 6 },
      },
      {
        name: "batch_no",
        label: t("NUSUK_BATCH_NUMBER"),
        type: "input",
      },
    ],
    [t, getLocalizedText]
  );

  const getRowActions = useCallback(
    (row) => {
      const actions = [];
      if (canListIncidentReports) {
        actions.push({
          key: "view",
          label: (
            <span className="d-flex align-items-center gap-2">
              <HugeiconsIcon size={18} icon={ViewIcon} />
              <span>{t("VIEW_REPORT")}</span>
            </span>
          ),
          onClick: () => handleViewReport(row),
        });
      }
      if (canResolveIncidentReports) {
        actions.push({
          key: "resolve_case",
          label: (
            <span className="d-flex align-items-center gap-2">
              <HugeiconsIcon size={18} icon={CheckmarkCircle01Icon} />
              <span>{t("END_CASE")}</span>
            </span>
          ),
          onClick: () => setOpen(row),
          danger: true,
        });
      }
      return actions;
    },
    [handleViewReport, t, canListIncidentReports, canResolveIncidentReports]
  );

  return (
    <div>
      <PageHeader title={t("NUSUK_CARDS_REPORTS")} sticky={false} />
      <Container fluid>
        <TableC
          data={rows}
          columns={columns}
          loading={loading}
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          searchPlaceholder={t("NUSUK_INCIDENT_SEARCH_PLACEHOLDER")}
          pagination={{
            current: currentPage,
            total: totalRows,
            pageSize,
            onChange: handlePageChange,
          }}
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          actions={getRowActions}
          renderActions={
            canViewExportBatchReport ? (
              <Button
                variant="primary"
                size="sm"
                type="button"
                disabled={isPrinting351 || selectedIncidentIds.length === 0}
                onClick={handlePrintForm351}
              >
                {t("NUSUK_FORM_351_PRINT")}
              </Button>
            ) : null
          }
          emptyStateTitle={t("NO_RESULTS")}
        />
      </Container>

      <Modal
        isOpen={Boolean(completeRow)}
        toggle={closeCompleteModal}
        centered
        backdrop={completeSubmitting ? "static" : true}
      >
        <ModalHeader toggle={closeCompleteModal}>{t("CONFIRM")}</ModalHeader>
        <ModalBody>
          <Stack gap={3}>
            <Form.Group>
              <Label className="fw-bold">{t("NUSUK_COMPLETE_CASE_RECIPIENT")}</Label>
              <AntInput
                value={completeRecipient}
                onChange={(e) => setCompleteRecipient(e.target.value)}
                placeholder={t("ENTER_PLACEHOLDER")}
                disabled={completeSubmitting}
              />
            </Form.Group>
            <Form.Group>
              <Label className="fw-bold">{t("REPORT_AUTHORITY")}</Label>
              <AntSelect
                showSearch
                optionFilterProp="label"
                placeholder={t("NUSUK_REPORT_AUTHORITY_LIST_PLACEHOLDER")}
                options={reportAuthorityOptions}
                value={completeReportAuthorityId ?? undefined}
                onChange={(v) => setCompleteReportAuthorityId(v ?? null)}
                disabled={completeSubmitting}
              />
            </Form.Group>
            <p className="mb-0 small text-muted">
              {t("NUSUK_COMPLETE_CASE_CONFIRM_QUESTION")}
            </p>
          </Stack>
        </ModalBody>
        <ModalFooter className="d-flex gap-2 justify-content-between flex-wrap">
          <Button
            variant="light"
            type="button"
            className="border"
            disabled={completeSubmitting}
            onClick={closeCompleteModal}
          >
            {t("CANCEL")}
          </Button>
          <Button
            variant="danger"
            type="button"
            disabled={completeSubmitting}
            onClick={handleSubmitCompleteCase}
          >
            <span className="d-inline-flex align-items-center gap-1">
              <HugeiconsIcon size={18} icon={CheckmarkCircle01Icon} />
              {t("CASE_COMPLETED")}
            </span>
          </Button>
        </ModalFooter>
      </Modal>

      <ResolveIncidentReportModal
        report={open}
        onClose={() => setOpen(null)}
        reportAuthorityOptions={reportAuthorityOptions}
        system={system}
      />
    </div>
  );
};
